import {
  checkClaimsWeb,
  EventrDate,
  HotelSearchAutoCompleteDto,
  SERPRegionRequestDtoSortByPref,
  spErrorTags,
  SPHotelInfo,
  SPHotelPage,
  SPSearchResp,
  TOrgConfig,
} from "@eventr/common";
import { IIpInfoState, ItemOptions } from "@eventr/common-storybook";
import { StateCreator } from "zustand";
import { DataAPI } from "../../data/Data";
import { EventrGatewayAPI } from "../../data/gateway.api";
import { useDeviceInfo } from "../../feat/appState/deviceInfo";
import i18n from "../../i18n/i18next";
import { compareBuildVersion } from "../../utils";
import { defaultAddSnack } from "../overlayState/overlayContext";
import { resolveAreaCurrency } from "../currency-defaults";
import { EphemeralStore } from "../useEphemeralStore";
import {
  bookingSliceDefaults,
  IBookingSlice,
  TFiltersObj,
  THotelRoom,
  TReturnReservationObj,
} from "./booking.interfaces";
import { getDateRangeFromEvent, getHotelsSearchData } from "./utils";

let pageNumber = 2;

/**
 * `[lon, lat]` to search around for a destination without a region id: its fixed point (an
 * airport), else wherever its label geocodes to.
 */
const destinationPoint = async (destination: ItemOptions | null): Promise<[number, number] | undefined> => {
  if (!destination || destination.id) return undefined;
  if (destination.obj?.lonlat) return destination.obj.lonlat;
  const address = await EventrGatewayAPI.address.findAddress(destination.label ?? "");
  if (address.status === "success" && address.data) return address.data.features[0]?.geometry?.coordinates;
  return undefined;
};

/** The currency hotels are priced in by default: the `booking-system` area's resolved default. */
export const defaultHotelCurrency = (ipInfo?: IIpInfoState): string =>
  resolveAreaCurrency("booking-system", ipInfo?.ipInfo?.countryCode);

export const createBookingSlice: StateCreator<EphemeralStore, [["zustand/devtools", never]], [], IBookingSlice> = (
  set,
  get,
) => ({
  ...bookingSliceDefaults,
  setBookingLoading: (setLoading) => {
    if (get().bookingLoading !== setLoading) {
      set({ bookingLoading: setLoading });
    }
  },
  setShowBookingService: (orgConfig: TOrgConfig) => {
    if (!compareBuildVersion(useDeviceInfo.getState().version.buildVersion, "1.4.3")) {
      if (get().showBookingService) {
        set({ showBookingService: false });
      }
      return;
    }
    if (!orgConfig.enableBookingService) {
      if (get().showBookingService) {
        set({ showBookingService: false });
      }
      return;
    }

    if (!checkClaimsWeb(["/"], ["editItinerary"])) {
      if (get().showBookingService) {
        set({ showBookingService: false });
      }
      return;
    }

    if (!get().showBookingService) {
      set({ showBookingService: true });
    }
    return;
  },
  getAdvertData: async (location: [number, number]) => {
    if (!get().showBookingService) {
      /// Not to be shown
      return;
    }

    const advertData = get().advertData;

    if (advertData && advertData.timeOfLastReq > 0) {
      /// Already has the data. advert no need to re-get.
      const now = new EventrDate().getUnixMs;
      if (now < advertData.timeOfLastReq + 120000) {
        return; /// Only repeat if 120 secs passed
      }
    }

    const advertDataReturn = await EventrGatewayAPI.booking.hotel.hotelSearchRecommended({
      location: location,
      returnLimit: 5,
    });

    if (!advertDataReturn || advertDataReturn.status !== "success" || (advertDataReturn.data?.length ?? 0) < 1) {
      return;
    }
    set({
      advertData: {
        SPHotelInfoShort: advertDataReturn.data ?? [],
        timeOfLastReq: new EventrDate().getUnixMs,
      },
    });
  },
  setDestination: (destination: ItemOptions | null) => {
    if (destination?.obj?.grType === "Hotels") {
      get().setSelectedHotel((destination.id as string) ?? "", "hotelplanner");
    }

    set({ destination: destination });
  },
  setDateRange: (dateRange: [string | null, string | null]) => {
    set({ dateRange: dateRange });
  },
  setEventLonLat: (lonlat: [number, number]) => {
    set({ eventLonLat: lonlat });
  },
  setRooms: (rooms: THotelRoom[]) => {
    set({ rooms: rooms });
  },
  getDestinationOptions: async (value: string, selectedEventInfo: any) => {
    // create a DTO object with country code, either of the event, or of the user if there is no event location
    // this needs to check if country is set to see if it should trust the country code
    let countryCode: string = selectedEventInfo?.OSMAddress?.address?.country_code;
    if (!countryCode || !selectedEventInfo?.OSMAddress?.address?.country) {
      countryCode = "";
    }

    // pass in a city to be a backup just in case coordinates aren't available
    // extract city from guaranteed value of timezone to use as a last resort
    let city: string = selectedEventInfo?.OSMAddress?.address?.city;
    if (!city) {
      city =
        selectedEventInfo?.OSMAddress?.timeZone?.substring(selectedEventInfo?.OSMAddress.timeZone.indexOf("/") + 1) ??
        "";
    }

    // TODO: this will only be empty if no address has been set, it cannot be cleared
    // address lookup rework should solve this, but for now, same as above, if country is empty, don't trust the longlat
    let coordinates: number[] = [selectedEventInfo.OSMAddress?.lon, selectedEventInfo.OSMAddress?.lat];
    if (!coordinates[0] || !selectedEventInfo?.OSMAddress?.address?.country) {
      coordinates = [];
    }

    const body: HotelSearchAutoCompleteDto = {
      keyword: value,
      countryCode: countryCode,
      city: city,
      location: coordinates,
    };
    const options = await EventrGatewayAPI.booking.hotel.autocomplete(body);
    if (options.status === "success") {
      /// always additive
      const optionsMapClone: ItemOptions[] = [];

      options.data?.hotels?.forEach((item) => {
        optionsMapClone.push({
          // value: item.id,
          label: item.name,
          obj: { ...item, grType: "Hotels" },
          id: item.id,
        });
      });
      options.data?.regions?.forEach((item) => {
        optionsMapClone.push({
          label: item.name,
          id: item.id,
          obj: { ...item, grType: "Regions" },
        });
      });

      optionsMapClone.sort((item) => {
        if (item.obj.grType === "Hotels") {
          return -1;
        } else {
          return 1;
        }
      });

      return optionsMapClone;
    }
    return [];
  },
  setSortByPreference: (sortByPreference: SERPRegionRequestDtoSortByPref) => {
    set({ sortByPreference: sortByPreference });
  },
  setFiltersObj: (filterObj: TFiltersObj) => {
    set({ filtersObj: filterObj });
  },
  setPriceBounds: (priceBounds: [number, number]) => {
    set({ priceBounds: priceBounds });
  },
  clearFilterObj: () => {
    set({
      filtersObj: {
        priceRange: [0, 10_000],
        currency: defaultHotelCurrency(),
        userRatingRange: [0, 10],
        starRating: 5,
      },
      priceBounds: [0, 10_000],
    });
  },
  setSearchResp: (searchResp: SPSearchResp | null) => {
    set({ searchResp: searchResp });
  },
  onEnterBookPage: async (selectedEventInfo: any, ipInfo: IIpInfoState) => {
    const { initialSearchHotels, getDestinationOptions, setDateRange, setDestination, setSearchResp, setEventLonLat } =
      get();
    setSearchResp(null);
    setDateRange([null, null]);
    setDestination(null);
    /// 1- get date range
    const range = getDateRangeFromEvent(selectedEventInfo);
    /// Dates need to be in future

    const now = new EventrDate();
    if (range && range[0].getUnixMs > now.getUnixMs && range[1].getUnixMs > now.getUnixMs) {
      setDateRange([range[0].toItinLocalDateString(), range[1].toItinLocalDateString()]);
    } else if (range && range[1].getUnixMs > now.getUnixMs) {
      setDateRange([new EventrDate().toItinLocalDateString(), range[1].toItinLocalDateString()]);
    }
    /// 2- get destination
    const options = await getDestinationOptions("", selectedEventInfo);
    const firstRegion = options.find((option) => option.obj.grType === "Regions");
    if (firstRegion) {
      setDestination(firstRegion);
    }
    /// 3- set currency from prefs
    get().syncCurrency(ipInfo);

    // Set the lon lat of the event so we search for hotels around us
    setEventLonLat([selectedEventInfo.OSMAddress?.lon ?? 0, selectedEventInfo.OSMAddress?.lat ?? 0]);

    if (range && firstRegion) {
      initialSearchHotels(false);
    }
  },
  syncCurrency: (ipInfo: IIpInfoState) => {
    set({ filtersObj: { ...get().filtersObj, currency: defaultHotelCurrency(ipInfo) } });
  },
  setSelectedHotel: (selectedHotel: string | null, provider: string) => {
    set({ selectedHotel: selectedHotel, provider: provider });
  },
  setHotelResp: (hotelResp: SPHotelPage | null) => {
    set({ hotelResp: hotelResp });
  },
  searchHotel: async () => {
    const { dateRange, selectedHotel, setBookingLoading, provider, rooms, filtersObj } = get();

    if (!dateRange || !selectedHotel) {
      spErrorTags("Tried to search for hotels without range or selected hotel", ["HotelBooking"]);
      return;
    }

    try {
      setBookingLoading(true);
      set({ hotelRatesStatus: "loading" });
      const resp = await EventrGatewayAPI.booking.hotel.hotelInfo({
        provider: provider ?? "",
        id: selectedHotel,
      });
      if (resp.status === "success") {
        set({
          hotelResp: {
            hotelInfo: resp.data ?? ({} as SPHotelInfo),
            rates: { rates: [] },
          },
        });
      } else {
        spErrorTags(resp.message ?? "Error with hotel request", ["HotelBooking"]);
        set({ hotelRatesStatus: "failed" });
        setBookingLoading(false);
        return;
      }
      const checkIn = new EventrDate(dateRange?.[0] ?? "").toItinLocalDateString() ?? "";
      const checkOut = new EventrDate(dateRange?.[1] ?? "").toItinLocalDateString() ?? "";

      const ratesResp = await EventrGatewayAPI.booking.hotel.hotelRates({
        check_in: checkIn,
        check_out: checkOut,
        provider: provider ?? "",
        id: selectedHotel,
        guests: rooms,
        currency: filtersObj.currency,
      });
      if (ratesResp.status === "success") {
        const currentHotelResp = get().hotelResp;

        if (currentHotelResp && currentHotelResp.hotelInfo.name === resp.data?.name) {
          set({
            hotelResp: {
              hotelInfo: currentHotelResp.hotelInfo,
              rates: ratesResp.data ?? { rates: [] },
            },
            hotelRatesStatus: "loaded",
          });
        } else {
          /// has already changed page or something, not matching.
        }
      } else {
        spErrorTags(ratesResp.message ?? "Error with rates request", ["HotelBooking"]);
        if (get().selectedHotel === selectedHotel) set({ hotelRatesStatus: "failed" });
      }
    } catch (error) {
      spErrorTags("Hotel details request failed", ["HotelBooking"], { error });
      if (get().selectedHotel === selectedHotel) set({ hotelRatesStatus: "failed" });
    } finally {
      setBookingLoading(false);
      return;
    }
  },
  loadMoreHotels: async () => {
    if (!get().showBookingService) {
      /// Not to be shown
      return;
    }

    const {
      sortByPreference,
      dateRange,
      destination,
      setSearchResp,
      rooms,
      filtersObj,
      setBookingLoading,
      eventLonLat,
    } = get();

    if (!sortByPreference || !dateRange || !dateRange[0] || !dateRange[1] || !destination) {
      return;
    }

    setBookingLoading(true);

    try {
      if (destination && destination.id) {
        if (destination?.obj?.grType === "Regions") {
          /// region search - filter search uses current filter values (does NOT change bounds)
          const initialHotelData = await getHotelsSearchData(
            new EventrDate(dateRange?.[0]).toItinLocalDateString() ?? "",
            new EventrDate(dateRange?.[1]).toItinLocalDateString() ?? "",
            rooms,
            pageNumber,
            sortByPreference,
            filtersObj,
            (destination?.id as number) ?? 0,
            destination.obj?.point,
            eventLonLat ?? [0, 0],
            false,
          );
          setSearchResp({
            hotels: [...(get().searchResp?.hotels || []), ...initialHotelData.hotels],
            totalHotels: (get().searchResp?.totalHotels ?? 0) + initialHotelData.totalHotels,
          });
        } else if (destination?.obj?.grType === "Hotels") {
          const resp = await EventrGatewayAPI.booking.hotel.hotelInfo({
            provider: "hotelplanner",
            id: destination?.obj?.id,
          });
          if (resp.status === "success") {
            set({
              hotelResp: {
                hotelInfo: resp.data!,
                rates: {
                  rates: [],
                },
              },
            });
          } else {
            spErrorTags(resp.message ?? "Error with hotel request", ["HotelBooking"]);
            return;
          }
        }
      } else {
        /// guess its address search - filter search uses current filter values (does NOT change bounds)
        const point = await destinationPoint(destination);

        const hotelData = await getHotelsSearchData(
          new EventrDate(dateRange?.[0]).toItinLocalDateString() ?? "",
          new EventrDate(dateRange?.[1]).toItinLocalDateString() ?? "",
          rooms,
          pageNumber,
          sortByPreference,
          filtersObj,
          (destination?.id as number) ?? 0,
          point,
          eventLonLat ?? [0, 0],
          false,
        );
        setSearchResp({
          hotels: [...(get().searchResp?.hotels || []), ...hotelData.hotels],
          totalHotels: (get().searchResp?.totalHotels ?? 0) + hotelData.totalHotels,
        });
      }
    } finally {
      pageNumber += 1;
      setBookingLoading(false);
    }
  },
  initialSearchHotels: async (showErrors: boolean = true) => {
    if (!get().showBookingService) {
      /// Not to be shown
      return;
    }

    const {
      sortByPreference,
      dateRange,
      destination,
      setSearchResp,
      rooms,
      filtersObj,
      setBookingLoading,
      eventLonLat,
      setPriceBounds,
    } = get();

    setSearchResp(null);

    if (!sortByPreference || !dateRange || !dateRange[0] || !dateRange[1]) {
      if (showErrors) {
        defaultAddSnack(i18n.t("booking.noRange"), "error");
      }
      return;
    }

    setBookingLoading(true);

    /// Proper search always uses full price range [0, 10000] then updates bounds from results
    try {
      if (destination && destination.id) {
        if (destination?.obj?.grType === "Regions") {
          const initialHotelData = await getHotelsSearchData(
            new EventrDate(dateRange?.[0]).toItinLocalDateString() ?? "",
            new EventrDate(dateRange?.[1]).toItinLocalDateString() ?? "",
            rooms,
            1,
            sortByPreference,
            filtersObj,
            (destination?.id as number) ?? 0,
            destination.obj?.point,
            eventLonLat ?? [0, 0],
            true,
          );
          setSearchResp(initialHotelData);
          // Update price bounds based on the most expensive hotel in results
          const maxPrice = initialHotelData.hotels.reduce(
            (max, hotel) => Math.max(max, Number(hotel.rate?.perNight) || 0),
            0,
          );
          if (maxPrice > 0) {
            setPriceBounds([0, maxPrice]);
            set({
              filtersObj: {
                ...filtersObj,
                priceRange: [0, maxPrice],
              },
            });
          }
        } else if (destination?.obj?.grType === "Hotels") {
          const resp = await EventrGatewayAPI.booking.hotel.hotelInfo({
            provider: "hotelplanner",
            id: destination?.obj?.id,
          });
          if (resp.status === "success") {
            set({
              hotelResp: {
                hotelInfo: resp.data!,
                rates: {
                  rates: [],
                },
              },
            });
          } else {
            spErrorTags(resp.message ?? "Error with hotel request", ["HotelBooking"]);
            return;
          }
        }
      } else {
        /// address search
        const point = await destinationPoint(destination);

        const hotelData = await getHotelsSearchData(
          new EventrDate(dateRange?.[0]).toItinLocalDateString() ?? "",
          new EventrDate(dateRange?.[1]).toItinLocalDateString() ?? "",
          rooms,
          1,
          sortByPreference,
          filtersObj,
          (destination?.id as number) ?? 0,
          point,
          eventLonLat ?? [0, 0],
          true,
        );
        setSearchResp(hotelData);
        // Update price bounds based on the most expensive hotel in results
        const maxPrice = hotelData.hotels.reduce((max, hotel) => Math.max(max, Number(hotel.rate?.perNight) || 0), 0);
        if (maxPrice > 0) {
          setPriceBounds([0, maxPrice]);
          set({
            filtersObj: {
              ...filtersObj,
              priceRange: [0, maxPrice],
            },
          });
        }
      }
    } finally {
      pageNumber = 2;
      setBookingLoading(false);
    }
  },
  searchHotelsWithFilter: async () => {
    if (!get().showBookingService) {
      return;
    }

    const {
      sortByPreference,
      dateRange,
      destination,
      setSearchResp,
      rooms,
      filtersObj,
      setBookingLoading,
      eventLonLat,
    } = get();

    setSearchResp(null);

    if (!sortByPreference || !dateRange || !dateRange[0] || !dateRange[1] || !destination) {
      return;
    }

    /// Filter search uses current filter values (does NOT change bounds)
    setBookingLoading(true);

    try {
      if (destination && destination.id) {
        if (destination?.obj?.grType === "Regions") {
          const hotelData = await getHotelsSearchData(
            new EventrDate(dateRange?.[0]).toItinLocalDateString() ?? "",
            new EventrDate(dateRange?.[1]).toItinLocalDateString() ?? "",
            rooms,
            1,
            sortByPreference,
            filtersObj,
            (destination?.id as number) ?? 0,
            destination.obj?.point,
            eventLonLat ?? [0, 0],
            false,
          );
          setSearchResp(hotelData);
        } else if (destination?.obj?.grType === "Hotels") {
          const resp = await EventrGatewayAPI.booking.hotel.hotelInfo({
            provider: "hotelplanner",
            id: destination?.obj?.id,
          });
          if (resp.status === "success") {
            set({
              hotelResp: {
                hotelInfo: resp.data!,
                rates: {
                  rates: [],
                },
              },
            });
          } else {
            spErrorTags(resp.message ?? "Error with hotel request", ["HotelBooking"]);
          }
        }
      } else {
        /// address search
        const point = await destinationPoint(destination);

        const hotelData = await getHotelsSearchData(
          new EventrDate(dateRange?.[0]).toItinLocalDateString() ?? "",
          new EventrDate(dateRange?.[1]).toItinLocalDateString() ?? "",
          rooms,
          1,
          sortByPreference,
          filtersObj,
          (destination?.id as number) ?? 0,
          point,
          eventLonLat ?? [0, 0],
          false,
        );
        setSearchResp(hotelData);
      }
    } finally {
      pageNumber = 2;
      setBookingLoading(false);
    }
  },
  setReturnReservationObj: (obj: TReturnReservationObj) => {
    set({ returnReservationObj: obj });
  },
  handleAddReservation: async (selectedOrg: string, onSuccess: () => void) => {
    const { returnReservationObj, setBookingLoading, setReturnReservationObj } = get();
    if (
      returnReservationObj &&
      returnReservationObj.confirm &&
      returnReservationObj.email &&
      returnReservationObj.selectedItinerary &&
      returnReservationObj.selectedItinerary.id
    ) {
      setBookingLoading(true);

      try {
        /// Add reservation to itinerary
        const resp = await EventrGatewayAPI.booking.hotel.addReservation({
          provider: "hotelplanner",
          HPItineraryNumber: returnReservationObj.confirm,
          email: returnReservationObj.email,
          itineraryID: returnReservationObj.selectedItinerary.id,
          orgID: selectedOrg,
        });

        if (resp && resp.status === "success") {
          /// Update app so visible in app
          const updateResp = await DataAPI.itineraries.updateValue(returnReservationObj.selectedItinerary.id);
          if (updateResp) {
            onSuccess();
          }
        }
      } catch {
        /// failed to add hotel reservation
        defaultAddSnack(i18n.t("booking.didntAddError"), "error");
      }
      setBookingLoading(false);
      setReturnReservationObj({
        ...returnReservationObj,
        justReturned: false,
      });
    } else {
      defaultAddSnack(i18n.t("booking.didntAddError"), "error");
    }
  },
});
